import React, { useEffect, useState } from 'react';
import { t, DAY_KEYS } from '../../i18n';
import './style.scss';

interface TimeRestriction {
  enabled: boolean;
  startTime: string;
  endTime: string;
  days: number[];
}

interface ShareData {
  blacklist?: string;
  redirectUrl?: string;
  timeRestriction?: TimeRestriction;
}

interface ChromeStorageResult {
  share_data?: string;
}

// 実際の拡張機能IDに置き換えてください（chrome://extensions/で確認）
const EXTENSION_ID = 'fpomkloanfedmhjnnjgkjmmbaefomnge';

const BlacklistDisplay: React.FC = () => {
  const [blacklist, setBlacklist] = useState<string[]>([]);
  const [redirectUrl, setRedirectUrl] = useState<string>('');
  const [editingRedirectUrl, setEditingRedirectUrl] = useState<string>('');
  const [timeRestriction, setTimeRestriction] = useState<TimeRestriction>({ enabled: false, startTime: '09:00', endTime: '18:00', days: [] });
  const [editingTimeRestriction, setEditingTimeRestriction] = useState<TimeRestriction>({ enabled: false, startTime: '09:00', endTime: '18:00', days: [] });
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [extensionAvailable, setExtensionAvailable] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [newDomain, setNewDomain] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);

  const defaultTimeRestriction: TimeRestriction = { enabled: false, startTime: '09:00', endTime: '18:00', days: [] };

  const applyTimeRestriction = (tr?: TimeRestriction) => {
    const val = tr || defaultTimeRestriction;
    setTimeRestriction(val);
    setEditingTimeRestriction(val);
  };

  // Chrome拡張機能のstorageに直接アクセス
  const loadDataFromChromeStorage = () => {
    return new Promise<boolean>((resolve) => {
      try {
        if (window.chrome?.storage?.sync) {
          window.chrome.storage.sync.get(['share_data'], (result: ChromeStorageResult) => {
            if (chrome.runtime.lastError) {
              resolve(false);
              return;
            }
            
            try {
              if (result.share_data) {
                const shareData: ShareData = JSON.parse(result.share_data);
                const blacklistData = shareData.blacklist
                  ? shareData.blacklist.split('\n').filter(domain => domain.trim() !== '')
                  : [];
                setBlacklist(blacklistData);
                setRedirectUrl(shareData.redirectUrl || '');
                setEditingRedirectUrl(shareData.redirectUrl || '');
                applyTimeRestriction(shareData.timeRestriction);
                setExtensionAvailable(true);
                resolve(true);
              } else {
                setBlacklist([]);
                setExtensionAvailable(true);
                resolve(true);
              }
            } catch (err) {
              resolve(false);
            }
          });
        } else {
          resolve(false);
        }
      } catch (err) {
        resolve(false);
      }
    });
  };

  // External Messagingを使用してデータを取得
  const loadDataFromExternalMessaging = () => {
    return new Promise<boolean>((resolve) => {
      try {
        if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
          
          chrome.runtime.sendMessage(EXTENSION_ID, {
            action: 'getBlacklistData'
          }, (response) => {
            if (chrome.runtime.lastError) {
              resolve(false);
              return;
            }
            
            if (response && response.success && response.data) {
              const blacklistData = response.data.blacklist
                ? response.data.blacklist.split('\n').filter((domain: string) => domain.trim() !== '')
                : [];
              setBlacklist(blacklistData);
              setRedirectUrl(response.data.redirectUrl || '');
              setEditingRedirectUrl(response.data.redirectUrl || '');
              applyTimeRestriction(response.data.timeRestriction);
              setExtensionAvailable(true);
              resolve(true);
            } else {
              resolve(false);
            }
          });
        } else {
          resolve(false);
        }
      } catch (err) {
        resolve(false);
      }
    });
  };

  // postMessageを使用してContent Scriptと通信
  const loadDataFromContentScript = () => {
    return new Promise<boolean>((resolve) => {
      const handleMessage = (event: MessageEvent) => {
        if (event.data.type === 'BLACKLIST_DATA_RESPONSE') {
          window.removeEventListener('message', handleMessage);
          
          if (event.data.error === 'Extension context invalidated') {
            setError(t('blErrorExtReload'));
            resolve(false);
            return;
          }
          
          if (event.data.success && event.data.data) {
            const blacklistData = event.data.data.blacklist
              ? event.data.data.blacklist.split('\n').filter((domain: string) => domain.trim() !== '')
              : [];
            setBlacklist(blacklistData);
            setRedirectUrl(event.data.data.redirectUrl || '');
            setEditingRedirectUrl(event.data.data.redirectUrl || '');
            applyTimeRestriction(event.data.data.timeRestriction);
            setExtensionAvailable(true);
            resolve(true);
          } else {
            resolve(false);
          }
        }
      };
      
      window.addEventListener('message', handleMessage);
      
      // Content Scriptにデータ要求
      window.postMessage({ type: 'BLACKLIST_GET_DATA' }, '*');
      
      // タイムアウト設定
      setTimeout(() => {
        window.removeEventListener('message', handleMessage);
        resolve(false);
      }, 3000);
    });
  };

  // データ更新関数
  const updateBlacklistData = async (newBlacklistData: string[], newRedirectUrl?: string, newTimeRestriction?: TimeRestriction) => {
    setSaving(true);
    const blacklistString = newBlacklistData.join('\n');
    const urlToSave = newRedirectUrl !== undefined ? newRedirectUrl : redirectUrl;
    const trToSave = newTimeRestriction !== undefined ? newTimeRestriction : timeRestriction;
    
    try {
      // 1. Content Script経由で更新を試す
      const success = await updateViaContentScript(blacklistString, urlToSave, trToSave);
      if (success) {
        setBlacklist(newBlacklistData);
        if (newRedirectUrl !== undefined) setRedirectUrl(newRedirectUrl);
        if (newTimeRestriction !== undefined) setTimeRestriction(newTimeRestriction);
        setSaving(false);
        return true;
      }

      // 2. External Messaging経由で更新を試す
      const externalSuccess = await updateViaExternalMessaging(blacklistString, urlToSave, trToSave);
      if (externalSuccess) {
        setBlacklist(newBlacklistData);
        if (newRedirectUrl !== undefined) setRedirectUrl(newRedirectUrl);
        if (newTimeRestriction !== undefined) setTimeRestriction(newTimeRestriction);
        setSaving(false);
        return true;
      }

      // 3. Chrome Storage直接更新を試す
      const storageSuccess = await updateViaChromeStorage(blacklistString, urlToSave, trToSave);
      if (storageSuccess) {
        setBlacklist(newBlacklistData);
        if (newRedirectUrl !== undefined) setRedirectUrl(newRedirectUrl);
        if (newTimeRestriction !== undefined) setTimeRestriction(newTimeRestriction);
        setSaving(false);
        return true;
      }

      setSaving(false);
      return false;
    } catch (error) {
      setSaving(false);
      return false;
    }
  };

  // Content Script経由でデータ更新
  const updateViaContentScript = (blacklistString: string, urlToSave: string, trToSave: TimeRestriction) => {
    return new Promise<boolean>((resolve) => {
      const handleMessage = (event: MessageEvent) => {
        if (event.data.type === 'BLACKLIST_UPDATE_RESPONSE') {
          window.removeEventListener('message', handleMessage);
          if (event.data.error === 'Extension context invalidated') {
            setError(t('blErrorExtReload'));
          }
          resolve(event.data.success);
        }
      };
      
      window.addEventListener('message', handleMessage);
      
      window.postMessage({
        type: 'BLACKLIST_UPDATE_DATA',
        data: { blacklist: blacklistString, redirectUrl: urlToSave, timeRestriction: trToSave }
      }, '*');
      
      setTimeout(() => {
        window.removeEventListener('message', handleMessage);
        resolve(false);
      }, 3000);
    });
  };

  // External Messaging経由でデータ更新
  const updateViaExternalMessaging = (blacklistString: string, urlToSave: string, trToSave: TimeRestriction) => {
    return new Promise<boolean>((resolve) => {
      try {
        if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
          chrome.runtime.sendMessage(EXTENSION_ID, {
            action: 'updateBlacklistData',
            blacklist: blacklistString,
            redirectUrl: urlToSave,
            timeRestriction: trToSave
          }, (response) => {
            if (chrome.runtime.lastError) {
              resolve(false);
              return;
            }
            resolve(response && response.success);
          });
        } else {
          resolve(false);
        }
      } catch (err) {
        resolve(false);
      }
    });
  };

  // Chrome Storage直接更新
  const updateViaChromeStorage = (blacklistString: string, urlToSave: string, trToSave: TimeRestriction) => {
    return new Promise<boolean>((resolve) => {
      try {
        if (window.chrome?.storage?.sync) {
          window.chrome.storage.sync.get(['share_data'], (result: ChromeStorageResult) => {
            if (chrome.runtime.lastError) {
              resolve(false);
              return;
            }
            
            try {
              let shareData: ShareData = {};
              if (result.share_data) {
                shareData = JSON.parse(result.share_data);
              }
              
              shareData.blacklist = blacklistString;
              shareData.redirectUrl = urlToSave;
              shareData.timeRestriction = trToSave;
              
              chrome.storage.sync.set({
                'share_data': JSON.stringify(shareData)
              }, () => {
                if (chrome.runtime.lastError) {
                  resolve(false);
                } else {
                  resolve(true);
                }
              });
            } catch (err) {
              resolve(false);
            }
          });
        } else {
          resolve(false);
        }
      } catch (err) {
        resolve(false);
      }
    });
  };

  // リダイレクト先URL保存
  const saveRedirectUrl = async () => {
    const success = await updateBlacklistData(blacklist, editingRedirectUrl);
    if (success) {
      setError('');
    } else {
      setError(t('blErrorRedirectFailed'));
    }
  };

  // 時間帯制限保存
  const saveTimeRestriction = async () => {
    const success = await updateBlacklistData(blacklist, undefined, editingTimeRestriction);
    if (success) {
      setError('');
    } else {
      setError(t('blErrorTimeFailed'));
    }
  };

  const toggleDay = (day: number) => {
    setEditingTimeRestriction(prev => {
      const days = prev.days.includes(day)
        ? prev.days.filter(d => d !== day)
        : [...prev.days, day];
      return { ...prev, days };
    });
  };

  const DAY_LABELS = DAY_KEYS.map(k => t(k));

  // ドメイン追加
  const addDomain = async () => {
    if (!newDomain.trim()) return;
    
    const domain = newDomain.trim();
    if (blacklist.includes(domain)) {
      setError(t('blErrorDuplicate'));
      return;
    }
    
    const newBlacklist = [...blacklist, domain];
    const success = await updateBlacklistData(newBlacklist);
    
    if (success) {
      setNewDomain('');
      setError('');
    } else {
      setError(t('blErrorAddFailed'));
    }
  };

  // ドメイン削除
  const removeDomain = async (domainToRemove: string) => {
    const newBlacklist = blacklist.filter(domain => domain !== domainToRemove);
    const success = await updateBlacklistData(newBlacklist);
    
    if (!success) {
      setError(t('blErrorDeleteFailed'));
    } else {
      setError('');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
      // 1. Content Scriptと通信を試す
      const contentSuccess = await loadDataFromContentScript();
      if (contentSuccess) {
        setLoading(false);
        return;
      }
      
      // 2. Chrome Storageに直接アクセスを試す
      const storageSuccess = await loadDataFromChromeStorage();
      if (storageSuccess) {
        setLoading(false);
        return;
      }

      // 3. External Messagingを試す
      const messagingSuccess = await loadDataFromExternalMessaging();
      if (messagingSuccess) {
        setLoading(false);
        return;
      }

      // 4. すべて失敗した場合はテストデータを表示
      setError(t('blErrorConnection'));
      setBlacklist(['example.com', 'test-site.com', 'blocked-domain.org', 'spam-site.net']);
      setLoading(false);
    };

    // 拡張機能準備完了の通知を待つ
    const handleExtensionReady = (event: MessageEvent) => {
      if (event.data.type === 'BLACKLIST_EXTENSION_READY') {
        loadData();
      }
    };

    window.addEventListener('message', handleExtensionReady);
    
    // すぐに試行も行う
    setTimeout(loadData, 500);

    return () => {
      window.removeEventListener('message', handleExtensionReady);
    };
  }, []);

  return (
    <div className="blacklist-container">
      <h2 className="header">
        {t('blTitle')}
      </h2>
      
      {error && (
        <div className="error-message">
          ⚠️ {error}
        </div>
      )}

      {/* リダイレクト先URL設定 */}
      <div className="add-domain-form">
        <h3 className="form-title">{t('blRedirectUrlTitle')}</h3>
        <div className="form-input-group">
          <input
            type="text"
            value={editingRedirectUrl}
            onChange={(e) => setEditingRedirectUrl(e.target.value)}
            placeholder={t('blRedirectUrlPlaceholder')}
            className="form-input"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                saveRedirectUrl();
              }
            }}
          />
          <button
            onClick={saveRedirectUrl}
            disabled={saving || editingRedirectUrl === redirectUrl}
            className="form-button"
          >
            {saving ? t('btnSaving') : t('btnSave')}
          </button>
        </div>
        {!redirectUrl && (
          <p style={{ fontSize: '12px', color: '#888', margin: '8px 0 0' }}>
            {t('blRedirectUrlNote')}
          </p>
        )}
      </div>

      {/* 時間帯制限設定 */}
      <div className="add-domain-form">
        <h3 className="form-title">{t('blTimeRestrictionTitle')}</h3>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '12px' }}>
          <input
            type="checkbox"
            checked={editingTimeRestriction.enabled}
            onChange={(e) => setEditingTimeRestriction(prev => ({ ...prev, enabled: e.target.checked }))}
          />
          <span>{t('blTimeRestrictionLabel')}</span>
        </label>
        {editingTimeRestriction.enabled && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <input
                type="time"
                value={editingTimeRestriction.startTime}
                onChange={(e) => setEditingTimeRestriction(prev => ({ ...prev, startTime: e.target.value }))}
                style={{ padding: '6px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <span>〜</span>
              <input
                type="time"
                value={editingTimeRestriction.endTime}
                onChange={(e) => setEditingTimeRestriction(prev => ({ ...prev, endTime: e.target.value }))}
                style={{ padding: '6px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
              {[1, 2, 3, 4, 5, 6, 0].map(day => (
                <label key={day} style={{
                  display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer',
                  padding: '4px 8px', borderRadius: '4px',
                  backgroundColor: editingTimeRestriction.days.includes(day) ? '#e3f2fd' : '#f5f5f5',
                  border: `1px solid ${editingTimeRestriction.days.includes(day) ? '#90caf9' : '#ddd'}`
                }}>
                  <input
                    type="checkbox"
                    checked={editingTimeRestriction.days.includes(day)}
                    onChange={() => toggleDay(day)}
                    style={{ display: 'none' }}
                  />
                  <span style={{ fontSize: '13px' }}>{DAY_LABELS[day]}</span>
                </label>
              ))}
            </div>
            {editingTimeRestriction.days.length === 0 && (
              <p style={{ fontSize: '12px', color: '#888', margin: '0 0 8px' }}>
                {t('blDayUnsetNote')}
              </p>
            )}
          </>
        )}
        <button
          onClick={saveTimeRestriction}
          disabled={saving || JSON.stringify(editingTimeRestriction) === JSON.stringify(timeRestriction)}
          className="form-button"
          style={{ width: 'auto' }}
        >
          {saving ? t('btnSaving') : t('btnSave')}
        </button>
      </div>

      {/* ドメイン追加フォーム */}
      <div className="add-domain-form">
        <h3 className="form-title">{t('blAddDomainTitle')}</h3>
        <div className="form-input-group">
          <input
            type="text"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            placeholder={t('blAddDomainPlaceholder')}
            className="form-input"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                addDomain();
              }
            }}
          />
          <button
            onClick={addDomain}
            disabled={saving || !newDomain.trim()}
            className="form-button"
          >
            {saving ? t('btnAdding') : t('btnAdd')}
          </button>
        </div>
      </div>
      
      {blacklist.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📝</div>
          <p className="empty-text">
            {t('blEmptyText')}
          </p>
        </div>
      ) : (
        <>
          <div className="domain-summary">
            <strong>{t('blDomainCount', { count: blacklist.length })}</strong>
          </div>
          
          <div className="domain-grid">
            {blacklist.map((domain, index) => (
              <div key={index} className="domain-item">
                <div className="domain-content">
                  <div className="domain-info">
                    <span className="domain-indicator"></span>
                    <span className="domain-name">
                      {domain}
                    </span>
                  </div>
                  <button
                    onClick={() => removeDomain(domain)}
                    disabled={saving}
                    className="delete-button"
                  >
                    {t('btnDelete')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      
      <div className="footer-info">
        {t('blFooterInfo')}
      </div>
    </div>
  );
};

export default BlacklistDisplay;
